import { useEffect, useState } from "react";
import { apiRequest } from "../api";
import { Button, Field, StatusPill } from "../components";
import { adminTime } from "./admin-order-types";
import { adminNavigate } from "./admin-navigation";

interface Health {
  checkedAt: string; healthy: boolean;
  alerts: { code: string; message: string; workspace: string }[];
  checks: { name: string; status: string; checkedAt: string; lastSuccessAt: string | null }[];
  bills: { businessDate: string; status: string; sha256: string | null; providerRows: number; discrepancyCount: number; checkedAt: string; lastError: string | null }[];
}
const checkNames: Record<string, string> = { worker: "后台任务进程", backup: "数据库备份", restore: "备份恢复演练" };
const billStatuses: Record<string, string> = { SUCCEEDED: "核对完成", NOT_AVAILABLE: "无可用账单", FAILED: "核对失败" };
export function AdminHealthWorkspace() {
  const [data, setData] = useState<Health | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(new Date(Date.now() + 8 * 3_600_000 - 86_400_000).toISOString().slice(0, 10));
  const [reason, setReason] = useState("人工复核每日支付宝账单");
  useEffect(() => {
    const controller = new AbortController();
    const load = () => void apiRequest<Health>("/api/admin/health", { signal: controller.signal }).then((result) => {
      if (!controller.signal.aborted) { setData(result); setError(""); }
    }).catch((caught) => { if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "无法读取运行状态"); });
    load(); const timer = setInterval(load, 10_000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [version]);
  return <article className="dashboard-card">
    <div className="card-title-row"><div><span className="card-kicker">业务与运行监控</span><h2>运行与账单核对</h2>
      <p>每小时检查待支付订单与已授权退款。账单从次日北京时间 10 点起核对，最近三天的无账单日期每天复查一次。账单差异进入人工复核，不会自动扣款或发放订阅。</p></div>
      <Button type="button" className="secondary-button" onClick={() => setVersion((value) => value + 1)}>刷新状态</Button></div>
    {error ? <p className="form-error" role="alert">{error}</p> : null}
    {data ? <><StatusPill tone={data.healthy ? "good" : "warn"}>{data.healthy ? "各项检查正常" : `${data.alerts.length} 项需要关注`}</StatusPill>
      <small> 最近检查 {adminTime(data.checkedAt)}</small>
      <ul>{data.alerts.map((alert) => <li key={alert.code}>{alert.message} <button type="button" className="text-button" onClick={() => adminNavigate(alert.workspace)}>查看</button></li>)}</ul>
      <dl className="admin-directory-facts">{Object.entries(checkNames).map(([name, label]) => {
        const check = data.checks.find((item) => item.name === name);
        return <div key={name}><dt>{label}</dt><dd>{check ? `${check.status === "OK" ? "成功" : "失败"} · 最近成功 ${adminTime(check.lastSuccessAt)}` : "尚未验证"}</dd></div>;
      })}</dl></> : <p role="status">读取运行状态…</p>}
    <h3>核对指定日期</h3><p>按北京时间选择过去 31 天内的账单。无可用账单可能表示当天没有交易或支付宝尚未生成文件，不能视为已核对一致。</p>
    <form className="admin-directory-search" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError(""); setNotice("");
      try { const result = await apiRequest<{ message: string }>("/api/admin/reconciliation", { method: "POST", body: JSON.stringify({ day, reason }) }); setNotice(result.message); setVersion((v) => v + 1); }
      catch (caught) { setError(caught instanceof Error ? caught.message : "无法提交核对"); } finally { setBusy(false); }
    }}><Field label="账单日期（北京时间）"><input type="date" required value={day} onChange={(event) => setDay(event.target.value)} /></Field>
      <Field label="核对备注"><input required minLength={3} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} /></Field><Button type="submit" busy={busy}>提交账单核对</Button></form>
    {notice ? <p role="status">{notice}；结果将在下方自动更新。</p> : null}
    <h3>最近账单记录</h3>{data?.bills.length === 0 ? <p>尚未执行账单核对。</p> : null}
    {data?.bills.map((bill) => <details className="admin-order-evidence" key={bill.businessDate}><summary>{bill.businessDate} · {billStatuses[bill.status] ?? bill.status} · {bill.providerRows} 条 · {bill.discrepancyCount} 项差异</summary>
      <p>最近核对 {adminTime(bill.checkedAt)}</p>{bill.lastError ? <p>{bill.lastError}</p> : null}
      {bill.sha256 ? <p>账单校验值 <code>{bill.sha256}</code></p> : null}
      {bill.discrepancyCount > 0 ? <button type="button" className="secondary-button" onClick={() => adminNavigate("operations", { "operations.kind": "discrepancies" })}>复核账务差异</button> : null}</details>)}
  </article>;
}
